/**
 * Pure conversion helpers between the shared `LeadFieldMappingTable`'s record
 * shape (`sourceKey -> target`) and the campaign contract's
 * `field_mapping.fields` array (`{ source: string[], target, value_map? }`,
 * quantix-api/docs/campaigns/field-mapping.md §5).
 *
 * The table itself only knows "one source key maps to one target"; the
 * campaign editor layers two extra concerns on top for combinable targets
 * (`name`, `notes`, §4.2):
 *  - `combineOrder`: the order in which several sources are concatenated for
 *    a combined target (the table has no notion of order, so this is tracked
 *    alongside the mapping and defaults to the order fields were mapped in).
 *  - `valueMaps`: the per-target value map for enum/select targets (§4.3).
 */

import type { FieldMappingEntry } from '@/api/campaigns/types';
import {
    NO_MAPPING_VALUE,
    type LeadFieldMapping,
} from '@/components/leads/mapping/mappingValidation';

export type CombineOrder = Record<string, string[]>;
export type ValueMaps = Record<string, Record<string, string>>;

export interface MappingEditorState {
    mapping: LeadFieldMapping;
    combineOrder: CombineOrder;
    valueMaps: ValueMaps;
}

/**
 * All source keys currently mapped to `target`, in "effective" order: the
 * tracked `combineOrder` first (filtered down to keys still mapped to this
 * target), then any mapped keys missing from it appended at the end (newly
 * mapped sources, or state hydrated before an order existed).
 */
export function effectiveCombineOrder(
    mapping: LeadFieldMapping,
    combineOrder: CombineOrder,
    target: string
): string[] {
    const mappedSources = Object.entries(mapping)
        .filter(([, mappedTarget]) => mappedTarget === target)
        .map(([sourceKey]) => sourceKey);

    const ordered = (combineOrder[target] ?? []).filter(key => mappedSources.includes(key));
    const missing = mappedSources.filter(key => !ordered.includes(key));

    return [...ordered, ...missing];
}

/** Swaps `sourceKey` with its neighbour in `combineOrder[target]` (a no-op past either end). */
export function moveSourceInCombineOrder(
    combineOrder: CombineOrder,
    target: string,
    sourceKey: string,
    direction: 'up' | 'down'
): CombineOrder {
    const list = combineOrder[target] ?? [];
    const index = list.indexOf(sourceKey);
    if (index === -1) return combineOrder;

    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= list.length) return combineOrder;

    const next = [...list];
    [next[index], next[swapWith]] = [next[swapWith], next[index]];

    return { ...combineOrder, [target]: next };
}

export interface BuildFieldEntriesParams {
    mapping: LeadFieldMapping;
    combineOrder: CombineOrder;
    valueMaps: ValueMaps;
    combinableTargets?: string[];
}

/** Converts the editor's record-shaped state into `field_mapping.fields`. */
export function recordMappingToFieldEntries({
    mapping,
    combineOrder,
    valueMaps,
    combinableTargets = [],
}: BuildFieldEntriesParams): FieldMappingEntry[] {
    const targetToSources = new Map<string, string[]>();

    Object.entries(mapping).forEach(([sourceKey, target]) => {
        if (!target || target === NO_MAPPING_VALUE) return;
        if (!targetToSources.has(target)) targetToSources.set(target, []);
        targetToSources.get(target)!.push(sourceKey);
    });

    const entries: FieldMappingEntry[] = [];

    targetToSources.forEach((naturalSources, target) => {
        const sources = combinableTargets.includes(target)
            ? effectiveCombineOrder(mapping, combineOrder, target)
            : naturalSources;

        const entry: FieldMappingEntry = { source: sources, target };
        const valueMap = valueMaps[target];
        if (valueMap && Object.keys(valueMap).length > 0) {
            entry.value_map = valueMap;
        }
        entries.push(entry);
    });

    return entries;
}

/** Converts `field_mapping.fields` (from the API) into the editor's record-shaped state. */
export function fieldEntriesToRecordMapping(
    fields: FieldMappingEntry[] | null | undefined
): MappingEditorState {
    const mapping: LeadFieldMapping = {};
    const combineOrder: CombineOrder = {};
    const valueMaps: ValueMaps = {};

    (fields ?? []).forEach(entry => {
        entry.source.forEach(sourceKey => {
            mapping[sourceKey] = entry.target;
        });
        if (entry.source.length > 1) {
            combineOrder[entry.target] = [...entry.source];
        }
        if (entry.value_map && Object.keys(entry.value_map).length > 0) {
            valueMaps[entry.target] = { ...entry.value_map };
        }
    });

    return { mapping, combineOrder, valueMaps };
}

/** Targets currently mapped from more than one source field — the ones that need a reorder panel. */
export function combinedTargetsInUse(
    mapping: LeadFieldMapping,
    combinableTargets: string[]
): string[] {
    const counts = new Map<string, number>();
    Object.values(mapping).forEach(target => {
        if (!target || target === NO_MAPPING_VALUE) return;
        counts.set(target, (counts.get(target) ?? 0) + 1);
    });
    return combinableTargets.filter(target => (counts.get(target) ?? 0) > 1);
}
